import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MKT } from '../../Marketing/links';
export default function AuctionSheet() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Every car at the big Japanese dealer auctions is inspected before it crosses the block, and the inspector’s report is the auction sheet. It is handwritten, in Japanese, and laid out a little differently at every auction house, but the same things are on all of them. Once you know where to look, you can read a Japanese auction sheet in a couple of minutes and know whether a lot deserves a bid."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The top strip: what, where and when"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Across the top you’ll find the lot number, the auction venue and date, the car’s name and trim, and the model code (for example ",
                    /*#__PURE__*/ _jsx("code", {
                        children: "GF-JZA80"
                    }),
                    "). The model code is worth reading: the letters after the dash identify the chassis, and the prefix tells you which emissions generation it belongs to."
                ]
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Next to it are the basics: engine size, transmission, fuel, doors, seats, colour and mileage. Transmission shorthand is the one that trips people up:"
            }),
            /*#__PURE__*/ _jsxs("ul", {
                children: [
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "FA / FAT"
                            }),
                            " — automatic, floor shifter. ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "CA"
                            }),
                            " — column automatic."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "F5 / 5F / MT"
                            }),
                            " — five-speed manual, floor shifter."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "CVT"
                            }),
                            " and ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "AT"
                            }),
                            " appear as written."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Reading the year: Japanese eras"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "The year on the sheet is usually the first registration year, written in the Japanese era. Convert it before you think about the 25-year rule:"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-post__table",
                children: /*#__PURE__*/ _jsxs("table", {
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Era"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Sheet shows"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Add"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Example"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("tbody", {
                            children: [
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Shōwa"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "S"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "1925"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "S63 = 1988"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Heisei"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "H"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "1988"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "H13 = 2001"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Reiwa"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "R"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "2018"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "R2 = 2020"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("blockquote", {
                children: [
                    "First registration is not the build date. For the",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/25-year-rule-jdm-cars-eligible-2026",
                        children: "25-year rule"
                    }),
                    " what counts is the month the car was manufactured, so a car first registered in H13 may have been built late in 2000 — or, rarely, registered well after it was built. Confirm the build month before you promise a customer a ship date."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Mileage and the warnings around it"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Mileage is in kilometres. Look for notes beside it: ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "走行不明"
                    }),
                    " means mileage unknown, and ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "メーター交換"
                    }),
                    " means the meter was replaced, sometimes with the previous reading noted. Either one should cut what you’ll pay, and you should tell the buyer."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The grades"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "A box near the top holds the overall grade (S, 6, 5, 4.5, 4, 3.5, 3, 2, 1 or R/RA), and nearby are letter grades for the interior and often the exterior, A to E. We cover what each one means in",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/japanese-auction-grades-explained",
                        children: "Japanese auction grades explained"
                    }),
                    ". The short version: the grade is the inspector’s summary, and the rest of the sheet is the evidence."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Equipment"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "A row of abbreviations or checkboxes records the options: ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "AC"
                    }),
                    " or",
                    ' ',
                    /*#__PURE__*/ _jsx("strong", {
                        children: "AAC"
                    }),
                    " (air conditioning, automatic climate), ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "PS"
                    }),
                    " (power steering), ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "PW"
                    }),
                    " (power windows), ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "SR"
                    }),
                    " (sunroof),",
                    ' ',
                    /*#__PURE__*/ _jsx("strong", {
                        children: "AW"
                    }),
                    " (alloy wheels), ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "TV"
                    }),
                    " / ",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "ナビ"
                    }),
                    " (navigation) and leather. On kei trucks especially, AC and PS are worth checking: plenty of work trucks were sold without them, and US buyers ask."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The damage map"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "A small drawing of the car carries the inspector’s marks: a letter for the kind of damage and a number for its size, 1 being small. The common ones:"
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-post__table",
                children: /*#__PURE__*/ _jsxs("table", {
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Code"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Means"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs("tbody", {
                            children: [
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "A1–A3"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Scratch"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "U1–U3"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Dent"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "B1–B3"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Dent with a scratch"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "W1–W3"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Wave or repaint: a panel has been repaired"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "S1–S2"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Rust"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "C1–C2"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Corrosion"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "P"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Paint marks or peeling"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "X / XX"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Panel needs replacing / has been replaced"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Y1–Y3"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Crack or hole"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "G"
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: "Stone chip in the glass"
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "Auction houses vary a little, and some add their own marks, so read the legend on the sheet when one is printed. A few A1s on a twenty-five-year-old car are nothing; W marks on several panels, or any S or C on the sills and underbody, are worth a closer look and a question to your agent."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The inspector’s notes"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "The free-text box is the most useful part of the sheet and the hardest to read. This is where the inspector writes what the grade doesn’t say: underbody rust (",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "下廻り錆"
                    }),
                    "), oil leaks (",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "オイル漏れ"
                    }),
                    "), modifications (",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "改"
                    }),
                    "), smells, warning lights, replaced parts, and a repair history (",
                    /*#__PURE__*/ _jsx("strong", {
                        children: "修復歴"
                    }),
                    ") with the panels involved. If the notes are long, assume they matter."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Checking the frame number"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "The sheet usually shows the frame (chassis) number, sometimes partly hidden. Match it to the listing and later to the export certificate and bill of lading: it is the one identifier that follows the car from the auction to your title."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Reading sheets faster"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "If you read dozens of sheets a week, Right Hand DMS reads them for you: every sheet comes back as structured English beside the original image, with the grades, mileage, options, damage map, notes and frame number pulled out. See",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.product('auction-sheets'),
                        children: "auction sheets in plain English"
                    }),
                    ", or the",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.guide,
                        children: "importing guide"
                    }),
                    " for where the sheet fits in the whole process."
                ]
            })
        ]
    });
}
